import React from "react";
import { Box } from "@mui/material";
import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { RationalRules } from "../../../new-reports/types/newReportTypes";
import {
  getDisplayCategoryName,
  getFieldDisplayLabel,
  getFieldDisplayValue,
  getOperatorDisplayText,
  capitalizeFirstLetter,
} from "../../utils/ruleFormatters";

type Props = {
  rules: RationalRules | undefined;
  labels: {
    rational: string;
    conditions: string;
  };
  categoryOptions: any;
  unitType?: number; // Optional unit type (1 = metric, 2 = imperial)
  // Optional font size overrides
  fontSizes?: {
    sectionTitle?: string;
    rationalHeader?: string;
    conditionCategory?: string;
    conditionItem?: string;
  };
};

const ServiceRationaleSection = ({
  rules,
  labels,
  categoryOptions,
  unitType,
  fontSizes,
}: Props) => {
  const theme: Theme = useTheme();

  // Default font sizes (from ServicePage)
  const defaultFontSizes = {
    sectionTitle: "11px",
    rationalHeader: "9px",
    conditionCategory: "9px",
    conditionItem: "9px",
  };

  // Merge with provided overrides
  const sizes = { ...defaultFontSizes, ...fontSizes };

  if (!rules || !rules.conditions || rules.conditions.length === 0) {
    return null;
  }

  return (
    <Box sx={styles.section}>
      <Box component="p" sx={styles.sectionTitle(theme, sizes.sectionTitle)}>
        {labels.rational}
      </Box>
      <Box sx={styles.rationalBox(theme)}>
        <Box
          component="p"
          sx={styles.rationalHeader(theme, sizes.rationalHeader)}
        >
          {labels.conditions}
          {rules.match && rules.conditions.filter((c) => c.valid).length > 1
            ? ` (${capitalizeFirstLetter(rules.match)})`
            : ""}
        </Box>
        <Box component="ol" sx={styles.conditionList}>
          {rules.conditions
            .filter((c) => c.valid)
            .map((condition, idx) => (
              <Box key={idx} sx={styles.conditionWrapper}>
                <Box
                  component="li"
                  sx={styles.conditionCategory(theme, sizes.conditionCategory)}
                >
                  {condition.object_name
                    ? getDisplayCategoryName(condition.object_name)
                    : ""}
                  {condition.fields.filter((f) => f.valid).length > 1 &&
                  condition.match
                    ? ` (${capitalizeFirstLetter(condition.match)})`
                    : ""}
                </Box>
                <Box component="ul" sx={styles.fieldList}>
                  {condition.fields
                    .filter((f) => f.valid)
                    .map((field, fieldIdx) => {
                      // Get user-friendly field label
                      const fieldLabel = getFieldDisplayLabel(
                        categoryOptions,
                        condition.object_name,
                        field.name
                      );

                      // Get user-friendly operator display
                      const operatorText = field.operator
                        ? getOperatorDisplayText(field.operator)
                        : "";

                      // Get user-friendly value display (with units or choice labels)
                      const valueDisplay = getFieldDisplayValue(
                        categoryOptions,
                        condition.object_name,
                        field.name,
                        field.value,
                        unitType
                      );

                      return (
                        <Box
                          component="li"
                          key={fieldIdx}
                          sx={styles.conditionItem(theme, sizes.conditionItem)}
                        >
                          <Box
                            component="span"
                            sx={{ fontFamily: "var(--font-Regular)" }}
                          >
                            {fieldLabel}
                            {operatorText && ` ${operatorText.toLowerCase()} `}
                            {valueDisplay}
                          </Box>
                        </Box>
                      );
                    })}
                </Box>
              </Box>
            ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceRationaleSection;

const styles = {
  section: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  sectionTitle: (theme: Theme, fontSize: string) => ({
    fontFamily: "var(--font-Regular)",
    fontSize,
    lineHeight: "normal",
    color: theme.colorTokens.N[90],
    margin: 0,
  }),
  rationalBox: (theme: Theme) => ({
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "12px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  }),
  rationalHeader: (theme: Theme, fontSize: string) => ({
    fontFamily: "var(--font-Light)",
    fontSize,
    lineHeight: "14px",
    color: theme.colorTokens.N[90],
    margin: 0,
  }),
  conditionWrapper: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
  },
  conditionList: {
    margin: 0,
    paddingLeft: "10px",
    listStyleType: "decimal",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "8px",
  },
  conditionCategory: (theme: Theme, fontSize: string) => ({
    fontFamily: "var(--font-Regular)",
    fontSize,
    lineHeight: "14px",
    color: theme.colorTokens.N[80],
  }),
  fieldList: {
    margin: 0,
    paddingLeft: "5px",
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    listStyleType: "disc",
  },
  conditionItem: (theme: Theme, fontSize: string) => ({
    fontFamily: "var(--font-Regular)",
    fontSize,
    lineHeight: "14px",
    color: theme.colorTokens.N[90],
  }),
};
